import React, {
  createContext,
  useState,
  ReactNode,
  useContext,
  useCallback,
} from 'react';
import { AppSnackbar } from '../services/AppSnackbar';
import { ExtrasContext } from './ExtrasContext';
type SnackbarType = 'error' | 'success' | 'info' | 'warning';
interface SnackMessage {
  message: string;
  type: SnackbarType | null;
  visible: boolean;
}
export interface snackMessage {
  message: string;
  type: SnackbarType;
  visible: boolean;
}
interface ErrorContextType {
  handleError: (message: string, type: SnackbarType, visible: boolean) => void;
  snackMessage: SnackMessage;
  setSnackMessage: React.Dispatch<React.SetStateAction<SnackMessage>>;
  resetSnackMessage: () => void;
  textInputError: { [key: string]: boolean };
  setTextInputError: React.Dispatch<
    React.SetStateAction<{ [key: string]: boolean }>
  >;
  quantityInputError: { [key: string]: boolean };
  setQuantityInputError: React.Dispatch<
    React.SetStateAction<{ [key: string]: boolean }>
  >;
}

const ErrorContext = createContext<ErrorContextType>({} as ErrorContextType);

interface ErrorContextProviderObject {
  children: ReactNode;
}

export const ErrorContextProvider: React.FC<ErrorContextProviderObject> = ({
  children,
}) => {
  const { networkAvailableGlobal } = useContext(ExtrasContext);
  const [snackMessage, setSnackMessage] = useState<SnackMessage>({
    message: '',
    type: null,
    visible: false,
  });

  const [textInputError, setTextInputError] = useState<{
    [key: string]: boolean;
  }>({});
  const [quantityInputError, setQuantityInputError] = useState<{
    [key: string]: boolean;
  }>({});

  // Normalize any bare product-id key to the composite cart-identity key
  // (`productId::` for non-variant, `productId::variantId` for variants).
  // Idempotent on already-composite keys. Keeps the error state in sync
  // across product grid, bottom sheet, and cart, which all read composite
  // keys but historically wrote bare ones.
  const normalizeErrorKey = useCallback(
    (key: string) => (key.includes('::') ? key : `${key}::`),
    [],
  );

  const wrappedSetTextInputError = useCallback(
    (updater: any) => {
      setTextInputError(prev => {
        const next =
          typeof updater === 'function' ? updater(prev) : updater;
        if (!next || typeof next !== 'object') return prev;
        let changed = false;
        const normalized: Record<string, boolean> = {};
        for (const k in next) {
          const nk = normalizeErrorKey(k);
          if (nk !== k) changed = true;
          normalized[nk] = next[k];
        }
        return changed ? normalized : next;
      });
    },
    [normalizeErrorKey],
  );

  const wrappedSetQuantityInputError = useCallback(
    (updater: any) => {
      setQuantityInputError(prev => {
        const next =
          typeof updater === 'function' ? updater(prev) : updater;
        if (!next || typeof next !== 'object') return prev;
        let changed = false;
        const normalized: Record<string, boolean> = {};
        for (const k in next) {
          const nk = normalizeErrorKey(k);
          if (nk !== k) changed = true;
          normalized[nk] = next[k];
        }
        return changed ? normalized : next;
      });
    },
    [normalizeErrorKey],
  );

  const resetSnackMessage = useCallback(() => {
    if (networkAvailableGlobal !== true) {
      return;
    }
    setSnackMessage({
      message: '',
      type: null,
      visible: false,
    });
  }, [networkAvailableGlobal]);

  const handleError = useCallback(
    (message: string, type: SnackbarType, visible: boolean) => {
      console.log('handling errors', message, type, visible);
      // Immediately show global snackbar via Paper host
      if (visible) {
        AppSnackbar.show(message, type);
      }
      setSnackMessage({
        message: message,
        type: type,
        visible: visible,
      });
    },
    [],
  );

  return (
    <ErrorContext.Provider
      value={{
        snackMessage,
        setSnackMessage,
        handleError,
        resetSnackMessage,
        textInputError,
        setTextInputError: wrappedSetTextInputError as React.Dispatch<
          React.SetStateAction<{ [key: string]: boolean }>
        >,
        quantityInputError,
        setQuantityInputError: wrappedSetQuantityInputError as React.Dispatch<
          React.SetStateAction<{ [key: string]: boolean }>
        >,      }}>
      {children}
    </ErrorContext.Provider>
  );
};

export default ErrorContext;
